"use client";

/**
 * Client cards for the SAVED /c/list category page (2026-08-19).
 *
 * The page (BroadListView) is SSR + crawlable, so each card MUST stay a real
 * <a href> - the crawler (and a JS-off client) follows it to /c/<slug> or the
 * live-search fallback. For a JS-on shopper we intercept the click and route it
 * to the GUARANTEED comparison: build (or reuse) the page from the pick's saved
 * offers, or - for an old page with none - a fresh lookup happens server-side, so
 * a tap can never dead-end on "no results". Falls back to the href on any miss.
 *
 * OFF (BROAD_OFFERS_COMPARE_ENABLED): the onClick is inert and the plain href
 * navigates exactly as today - the rollback state.
 */

import type { Route } from "next";
import { useRouter } from "next/navigation";
import { useState } from "react";

import type { BroadProduct } from "@/lib/api/broad";
import { compareFromOffers } from "@/lib/api/broad";
import { cleanTitleForSearch } from "@/lib/broad-format";
import {
  BROAD_OFFERS_COMPARE_ENABLED,
  BROAD_STREAM_COMPARE_ENABLED,
} from "@/lib/flags";

import { LIST_CARD_CLASS, ListCardContent } from "./ListCardContent";
import { SearchingSceneOverlay } from "./SearchingSceneOverlay";

interface Props {
  products: BroadProduct[];
  listSlug: string;
  /** Per-product crawlable fallback href (compareHref), one per product. */
  hrefs: string[];
}

export function BroadListCards({ products, listSlug, hrefs }: Props) {
  const router = useRouter();
  const [picked, setPicked] = useState(false);

  // A committed tap shows the same searching scene the rest of the app uses.
  if (picked) {
    return <SearchingSceneOverlay loaded={0} failed={0} total={0} isDone={false} />;
  }

  const onTap = async (p: BroadProduct, fallbackHref: string, e: React.MouseEvent) => {
    // Already-built page: let the crawlable <a href=/c/slug> navigate.
    if (p.comparison_slug) return;
    // Seeded STREAMING tap (2026-08-19): open /compare seeded with this pick's
    // known shops so the real final prices stream in and it can't dead-end. The
    // <a href> stays for crawlers / JS-off; we intercept the JS-on click.
    if (BROAD_STREAM_COMPARE_ENABLED) {
      e.preventDefault();
      setPicked(true);
      const q = cleanTitleForSearch(p.title);
      router.push(
        (`/compare?q=${encodeURIComponent(q)}&from_chip=1` +
          `&seed_list=${encodeURIComponent(listSlug)}` +
          `&seed_title=${encodeURIComponent(p.title)}`) as Route,
      );
      return;
    }
    // Legacy listed-only offers tap; off -> the plain href navigates as today.
    if (!BROAD_OFFERS_COMPARE_ENABLED) return;
    e.preventDefault();
    setPicked(true);
    const res = await compareFromOffers(listSlug, p.title, "AU");
    router.push((res?.slug ? `/c/${res.slug}` : fallbackHref) as Route);
  };

  return (
    <section className="mt-6 space-y-3">
      {products.map((p, i) => (
        <a
          key={p.title + i}
          href={hrefs[i]}
          onClick={(e) => onTap(p, hrefs[i], e)}
          className={LIST_CARD_CLASS}
        >
          <ListCardContent product={p} />
        </a>
      ))}
    </section>
  );
}
